<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <!-- Title -->
                <p v-if="name"><b>{{ $t('faqIntro') }}</b> {{ name }}</p>
                <!-- Language dropdown -->
                <LanguageDropdown class="align-self-end" v-model="currentLanguage"></LanguageDropdown>
                <div class="d-flex flex-column py-2">
                    <!-- Titles -->
                    <div role="group" class="form-group col-12 p-0">
                        <label class=""> {{ $t('titleMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="faq.names[currentLanguage.key]" :invalid-feedback="errorOutput['Names']" :is-valid="genericValidator(faq.names, attemptedSubmit) && errorValidator('Names')"></CInput>
                    </div>
                    <!-- Faqs -->
                    <div class="d-flex align-items-center col-12 p-0 gap-2 flex-wrap">
                        <label> {{ $t('tabs') }}</label>
                        <button type="button" class="btn btn-secondary mx-2" @click="addFaq">{{ $t('addFaq') }}</button>
                        <button type="button" class="btn btn-secondary" @click="removeCurrentFaq">{{ $t('removeFaq') }}</button>
                    </div>
                    <CTabs class="col-12 p-0 mt-3" v-if="faq.items && faq.items.length" variant="pills" :activeTab="currentFaq">
                        <CTab v-for="(faq, index) in faq.items" :key="index" :title="$t('faq') + ' ' + (index + 1)">
                            <div class="d-flex flex-column mt-2">
                                <!-- Question -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('questionMandatory') }}</label>
                                    <CInput class="m-0" type="text" v-model="faq.questions[currentLanguage.key]" :invalid-feedback="errorOutput.Faqs[index].Questions" :is-valid="genericValidator(faq.questions, attemptedSubmit) && errorValidator(`Faqs[${index}].Questions`)"></CInput>
                                </div>
                                <!-- Answer -->
                                <div role="group" class="form-group col-12 mb-0">
                                    <label class=""> {{ $t('answerMandatory') }}</label>
                                    <vue-editor v-model="faq.answers[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(faq.answers, attemptedSubmit) == false || errorValidator(`Faqs[${index}].Answers`) == false }"></vue-editor>
                                    <div v-if="errorOutput.Faqs[index].Answers" class="invalid-feedback">{{errorOutput.Faqs[index].Answers}}</div>
                                </div>
                            </div>
                        </CTab>
                    </CTabs>
                </div>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapState } from 'vuex'
import router from '@/router/index';
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import FormLoading from '@/controls/FormLoading.vue'
import FaqItems from "@/classes/FaqItems";
import Faq from "@/classes/Faq";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import { VueEditor } from "vue2-editor";

  export default {
        components: { LanguageDropdown, ConfirmationPopUp, VueEditor, FormLoading},
    props:{    
        baseFaq:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            faq: new Faq({}, []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            currentFaq: 0,
        }
    },
    mounted: function()
    {
        this.resetFaq();
    },
    computed:
    {
        ...mapState('faqs', ['putFaqOutput']),
        ...mapState('faqs', ['postFaqOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.faq.names)) { if(!value) { result['Names'] = this.$i18n.t("validateBothTranslations"); break; }; }

            // Validate Question and Answer on FAQ
            result.Faqs = [];
            const self = this;

            this.faq.items.forEach(function(item){
                var error = {};

                for (const [key, value] of Object.entries(item.questions)) {
                    if(!value)
                    {
                        error['Questions'] = self.$i18n.t("validateBothTranslations");
                        break;
                    }
                }

                for (const [key, value] of Object.entries(item.answers)) {
                    if(!value)
                    {
                        error['Answers'] = self.$i18n.t("validateBothTranslations");
                        break;
                    }
                }

                result.Faqs.push(error);
            });

            return result;

        },
        name: function () {
            return this.baseFaq || this.baseName ? (this.baseFaq ? this.baseFaq.name : this.baseName) : null;
        }
    },
    methods:{
        genericValidator,
        errorValidator,
        addFaq()
        {
            var questions = {};
            var answers = {};

            CONSTANTS.supportedLanguages.forEach(language => { questions[language.key] = ""; answers[language.key] = "" });

            this.faq.items.push(new FaqItems(questions, answers));
        },
        removeCurrentFaq()
        {
            this.faq.items.splice(this.currentFaq, 1);
        },
        resetFaq(){
            if(!this.baseFaq)
            {
                var names = {};
                CONSTANTS.supportedLanguages.forEach(language => names[language.key] = "");
                this.faq = new Faq(names, []);
            }
            else
            {
                this.faq = deepClone(new Faq, this.baseFaq);    
            }
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.faq.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.faq);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "Faqs"});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        }
    },
    watch:
    {
        putFaqOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postFaqOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseFaq: function()
        {
            this.resetFaq();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}
</style> 